import React from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronDown, LogOut, User, Building2, Check, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFacility } from "@/context/FacilityContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

export function Header({ onMenu }) {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const isPlatform = auth?.is_platform_admin;
  const facilityCtx = useFacility();
  const user = auth?.user;
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <header className="h-16 shrink-0 bg-white border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-30">
      <button className="lg:hidden text-slate-600" onClick={onMenu} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>

      {isPlatform ? (
        <div className="flex items-center gap-2 text-sm">
          <span className="h-7 w-7 rounded-md bg-blue-100 text-blue-700 grid place-items-center"><Building2 className="h-4 w-4" /></span>
          <span className="font-medium text-slate-800">Mediva Solutions — Platform</span>
        </div>
      ) : (
        <div className="flex items-center gap-3 min-w-0">
          <div className="hidden sm:block text-xs text-slate-400 font-mono uppercase tracking-wider">
            {auth?.organisation?.name}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-300 bg-slate-50 max-w-[280px]" data-testid="facility-switcher-dropdown">
                <Building2 className="h-4 w-4 text-blue-600 shrink-0" />
                <span className="text-sm font-medium text-slate-800 truncate">{facilityCtx?.activeFacility?.name || "Select facility"}</span>
                <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <DropdownMenuLabel className="text-xs text-slate-500">Switch facility</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {(facilityCtx?.facilities || []).map((f) => (
                <DropdownMenuItem key={f.id} onClick={() => facilityCtx.switchFacility(f.id)} data-testid={`facility-option-${f.id}`} className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-slate-800">{f.name}</span>
                    <span className="text-[11px] text-slate-400 font-mono">{f.code} · {f.city}</span>
                  </div>
                  {facilityCtx.activeId === f.id && <Check className="h-4 w-4 text-blue-600" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <div className="ml-auto flex items-center gap-1.5 sm:gap-3">
        <Button variant="ghost" size="icon" className="relative text-slate-500" data-testid="notifications-btn">
          <Bell className="h-5 w-5" />
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-blue-600" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-md hover:bg-slate-50" data-testid="user-profile-menu">
              <span className="h-8 w-8 rounded-md bg-slate-900 text-white grid place-items-center text-xs font-semibold">{initials}</span>
              <div className="hidden sm:block text-left leading-tight">
                <div className="text-sm font-medium text-slate-800 max-w-[140px] truncate">{user?.name}</div>
                <div className="text-[11px] text-slate-400">{isPlatform ? "Platform Super Admin" : (auth?.memberships?.[0]?.role_name || "User")}</div>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-sm font-medium text-slate-800">{user?.name}</div>
              <div className="text-xs text-slate-400 font-normal">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-profile"><User className="h-4 w-4 mr-2" /> Profile</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={doLogout} className="text-red-600 focus:text-red-700" data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
